import React, { useState } from 'react';
import './SalesReports.css';

const SalesReports = () => {
  // Sales metrics data
  const [metrics] = useState({
    leadsGenerated: 24,
    enquiriesConverted: 18,
    dealsWon: 12,
    revenueCollected: 8460000,
    pendingPayments: 3240000,
    activeProjects: 8,
    winRate: 67,
    avgDealSize: 705000,
    conversionRate: 75,
    collectionRate: 72
  });

  // Executive-wise performance data
  const [executives] = useState([
    { id: 1, name: 'Priya Sharma', role: 'Senior Executive', leads: 8, converted: 7, dealsWon: 5, revenue: 4600000, target: 5000000, avatar: 'P', color: '#3b82f6', trend: '+12%' },
    { id: 2, name: 'Karan Verma', role: 'Executive', leads: 6, converted: 4, dealsWon: 3, revenue: 2160000, target: 3500000, avatar: 'K', color: '#10b981', trend: '+8%' },
    { id: 3, name: 'Neha Gupta', role: 'Manager', leads: 5, converted: 4, dealsWon: 2, revenue: 1200000, target: 4000000, avatar: 'N', color: '#8b5cf6', trend: '+5%' },
    { id: 4, name: 'Raj Malhotra', role: 'Sales Executive', leads: 3, converted: 2, dealsWon: 1, revenue: 500000, target: 2500000, avatar: 'R', color: '#ef4444', trend: '-2%' },
    { id: 5, name: 'Aarav Mehta', role: 'Admin', leads: 2, converted: 1, dealsWon: 1, revenue: 0, target: 0, avatar: 'A', color: '#4f46e5', trend: '0%' }
  ]);

  // Monthly revenue data
  const monthlyRevenue = [
    { month: 'Jan', revenue: 420000, target: 400000 },
    { month: 'Feb', revenue: 680000, target: 450000 },
    { month: 'Mar', revenue: 1120000, target: 500000 },
    { month: 'Apr', revenue: 1460000, target: 550000 },
    { month: 'May', revenue: 980000, target: 600000 },
    { month: 'Jun', revenue: 1250000, target: 650000 }
  ];

  // Deal pipeline data
  const pipelineData = [
    { name: 'Won Deals', value: 12, color: '#10b981', percentage: 50 },
    { name: 'In Negotiation', value: 6, color: '#4f46e5', percentage: 25 },
    { name: 'Proposal Sent', value: 4, color: '#3b82f6', percentage: 17 },
    { name: 'Initial Contact', value: 2, color: '#8b5cf6', percentage: 8 }
  ];

  // Revenue by executive
  const revenueByExecutive = [
    { name: 'Priya Sharma', value: 4600000, color: '#3b82f6', percentage: 54 },
    { name: 'Karan Verma', value: 2160000, color: '#10b981', percentage: 26 },
    { name: 'Neha Gupta', value: 1200000, color: '#8b5cf6', percentage: 14 },
    { name: 'Raj Malhotra', value: 500000, color: '#ef4444', percentage: 6 }
  ];

  // Calculate totals
  const totalRevenue = executives.reduce((sum, exec) => sum + exec.revenue, 0);
  const totalTarget = executives.reduce((sum, exec) => sum + exec.target, 0);
  const overallAchievement = totalTarget > 0 ? (totalRevenue / totalTarget) * 100 : 0;

  // Format currency
  const formatCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  // Get max revenue for chart
  const maxRevenue = Math.max(...monthlyRevenue.map(m => Math.max(m.revenue, m.target)), 1);

  // Simple Pie Chart Component
  const SimplePieChart = ({ data, size = 180, title }) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    if (total === 0) return <div className="sr-no-data">No data available</div>;

    let currentAngle = 0;
    const centerX = size / 2;
    const centerY = size / 2;
    const radius = size / 2 - 15;

    return (
      <div className="sr-pie-container">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {data.map((item, index) => {
            const angle = (item.value / total) * 360;
            const startAngle = currentAngle;
            const endAngle = currentAngle + angle;
            currentAngle += angle;

            const startRad = (startAngle - 90) * Math.PI / 180;
            const endRad = (endAngle - 90) * Math.PI / 180;

            const x1 = centerX + radius * Math.cos(startRad);
            const y1 = centerY + radius * Math.sin(startRad);
            const x2 = centerX + radius * Math.cos(endRad);
            const y2 = centerY + radius * Math.sin(endRad);

            const largeArc = angle > 180 ? 1 : 0;

            return (
              <path
                key={index}
                d={`M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`}
                fill={item.color}
                className="sr-pie-slice"
              />
            );
          })}
          <circle cx={centerX} cy={centerY} r={radius - 25} fill="white" />
          <text x={centerX} y={centerY - 5} textAnchor="middle" className="sr-pie-total-value" fill="#1f2937" fontSize="18" fontWeight="bold">
            {total}
          </text>
          <text x={centerX} y={centerY + 15} textAnchor="middle" className="sr-pie-total-label" fill="#6b7280" fontSize="10">
            {title || 'Total'}
          </text>
        </svg>
      </div>
    );
  };

  // Simple Bar Chart Component
  const BarChart = ({ data, maxValue, formatValue }) => {
    return (
      <div className="sr-bar-chart-container">
        {data.map((item, index) => (
          <div key={index} className="sr-bar-chart-item">
            <div className="sr-bar-chart-bars">
              <div className="sr-bar-chart-bar-wrapper">
                <div 
                  className="sr-bar-chart-bar sr-bar-revenue" 
                  style={{ height: `${(item.revenue / maxValue) * 150}px` }}
                >
                  <span className="sr-bar-value">{formatValue(item.revenue)}</span>
                </div>
                <div className="sr-bar-label">{item.month}</div>
              </div>
              <div className="sr-bar-chart-bar-wrapper">
                <div 
                  className="sr-bar-chart-bar sr-bar-target" 
                  style={{ height: `${(item.target / maxValue) * 150}px` }}
                >
                  <span className="sr-bar-value-target">{formatValue(item.target)}</span>
                </div>
                <div className="sr-bar-label">Target</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="sr-container">
      <div className="sr-wrapper">
        {/* Header */}
        <div className="sr-header">
          <div>
            <h1 className="sr-title">📊 Sales Executive Performance Dashboard</h1>
            <p className="sr-subtitle">Real-time performance metrics · Team & Individual Analytics</p>
          </div>
          <select className="sr-period-select">
            <option>This Quarter</option>
            <option>This Month</option>
            <option>This Year</option>
          </select>
        </div>

        {/* Achievement Banner */}
        <div className="sr-achievement-banner">
          <div className="sr-achievement-left">
            <span className="sr-achievement-icon">🎯</span>
            <div>
              <div className="sr-achievement-label">Overall Team Achievement</div>
              <div className="sr-achievement-percent">{overallAchievement.toFixed(1)}%</div>
            </div>
          </div>
          <div className="sr-achievement-right">
            <div className="sr-progress-bar-container">
              <div className="sr-progress-bar" style={{ width: `${Math.min(overallAchievement, 100)}%` }}></div>
            </div>
            <div className="sr-achievement-target">
              Target: {formatCurrency(totalTarget)} | Achieved: {formatCurrency(totalRevenue)}
            </div>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="sr-metrics-grid">
          <div className="sr-metric-card">
            <div className="sr-metric-icon">📊</div>
            <div className="sr-metric-info">
              <div className="sr-metric-label">LEADS GENERATED</div>
              <div className="sr-metric-value">{metrics.leadsGenerated}</div>
              <div className="sr-metric-trend up">↑ +8 vs last month</div>
            </div>
          </div>
          <div className="sr-metric-card">
            <div className="sr-metric-icon">📝</div>
            <div className="sr-metric-info">
              <div className="sr-metric-label">ENQUIRIES CONVERTED</div>
              <div className="sr-metric-value">{metrics.enquiriesConverted}</div>
              <div className="sr-metric-trend up">↑ +5 vs last month</div>
            </div>
          </div>
          <div className="sr-metric-card">
            <div className="sr-metric-icon">🏆</div>
            <div className="sr-metric-info">
              <div className="sr-metric-label">DEALS WON</div>
              <div className="sr-metric-value">{metrics.dealsWon}</div>
              <div className="sr-metric-trend up">↑ +3 vs last month</div>
            </div>
          </div>
          <div className="sr-metric-card">
            <div className="sr-metric-icon">💰</div>
            <div className="sr-metric-info">
              <div className="sr-metric-label">REVENUE COLLECTED</div>
              <div className="sr-metric-value sr-success">{formatCurrency(metrics.revenueCollected)}</div>
            </div>
          </div>
          <div className="sr-metric-card">
            <div className="sr-metric-icon">⏳</div>
            <div className="sr-metric-info">
              <div className="sr-metric-label">PENDING PAYMENTS</div>
              <div className="sr-metric-value sr-warning">{formatCurrency(metrics.pendingPayments)}</div>
            </div>
          </div>
          <div className="sr-metric-card">
            <div className="sr-metric-icon">📁</div>
            <div className="sr-metric-info">
              <div className="sr-metric-label">ACTIVE PROJECTS</div>
              <div className="sr-metric-value">{metrics.activeProjects}</div>
            </div>
          </div>
        </div>

        {/* Executive Table */}
        <div className="sr-executive-section">
          <h3 className="sr-section-title">👥 Executive Performance Overview</h3>
          <div className="sr-table-responsive">
            <table className="sr-executive-table">
              <thead>
                <tr>
                  <th>Executive</th>
                  <th>Role</th>
                  <th>Leads</th>
                  <th>Converted</th>
                  <th>Deals Won</th>
                  <th>Revenue</th>
                  <th>Target</th>
                  <th>Achievement</th>
                  <th>Trend</th>
                </tr>
              </thead>
              <tbody>
                {executives.map((exec) => (
                  <tr key={exec.id}>
                    <td className="sr-executive-cell">
                      <div className="sr-executive-avatar" style={{ backgroundColor: exec.color }}>
                        {exec.avatar}
                      </div>
                      <span className="sr-executive-name">{exec.name}</span>
                    </td>
                    <td><span className="sr-role-badge">{exec.role}</span></td>
                    <td>{exec.leads}</td>
                    <td>{exec.converted}</td>
                    <td>{exec.dealsWon}</td>
                    <td className="sr-revenue-cell">{formatCurrency(exec.revenue)}</td>
                    <td>{formatCurrency(exec.target)}</td>
                    <td>
                      <div className="sr-progress-wrapper">
                        <div className="sr-progress-bar-small">
                          <div 
                            className="sr-progress-fill" 
                            style={{ width: `${exec.target > 0 ? (exec.revenue / exec.target) * 100 : 0}%` }}
                          ></div>
                        </div>
                        <span className="sr-progress-percent">
                          {exec.target > 0 ? ((exec.revenue / exec.target) * 100).toFixed(0) : 0}%
                        </span>
                      </div>
                    </td>
                    <td className={
                      exec.trend.includes('+') ? 'sr-trend-up' : 
                      exec.trend.includes('-') ? 'sr-trend-down' : 
                      'sr-trend-neutral'
                    }>
                      {exec.trend}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Charts Row */}
        <div className="sr-charts-row">
          {/* Pipeline Chart */}
          <div className="sr-chart-card">
            <h3 className="sr-chart-title">📊 Deal Pipeline Status</h3>
            <div className="sr-chart-content">
              <SimplePieChart data={pipelineData} size={200} title="Total Deals" />
              <div className="sr-chart-legend">
                {pipelineData.map((item, idx) => (
                  <div key={idx} className="sr-legend-item">
                    <div className="sr-legend-color" style={{ backgroundColor: item.color }}></div>
                    <div className="sr-legend-text">
                      <span>{item.name}</span>
                      <span className="sr-legend-value">{item.value} ({item.percentage}%)</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Revenue Distribution Chart */}
          <div className="sr-chart-card">
            <h3 className="sr-chart-title">💰 Revenue by Executive</h3>
            <div className="sr-chart-content">
              <SimplePieChart data={revenueByExecutive} size={200} title="Total Revenue" />
              <div className="sr-chart-legend">
                {revenueByExecutive.map((item, idx) => (
                  <div key={idx} className="sr-legend-item">
                    <div className="sr-legend-color" style={{ backgroundColor: item.color }}></div>
                    <div className="sr-legend-text">
                      <span>{item.name}</span>
                      <span className="sr-legend-value">{item.percentage}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Monthly Trend Chart */}
        <div className="sr-trend-card">
          <h3 className="sr-chart-title">📈 Monthly Revenue Trend vs Target</h3>
          <div className="sr-trend-legend">
            <div className="sr-trend-legend-item">
              <div className="sr-trend-color sr-trend-revenue"></div>
              <span>Actual Revenue</span>
            </div>
            <div className="sr-trend-legend-item">
              <div className="sr-trend-color sr-trend-target"></div>
              <span>Target</span>
            </div>
          </div>
          <div className="sr-bar-chart-wrapper">
            <BarChart data={monthlyRevenue} maxValue={maxRevenue} formatValue={formatCurrency} />
          </div>
        </div>

        {/* Quick Stats */}
        <div className="sr-quick-stats">
          <div className="sr-quick-stat">
            <div className="sr-quick-stat-icon">📈</div>
            <div>
              <div className="sr-quick-stat-label">Conversion Rate</div>
              <div className="sr-quick-stat-value">{metrics.conversionRate}%</div>
            </div>
          </div>
          <div className="sr-quick-stat">
            <div className="sr-quick-stat-icon">🎯</div>
            <div>
              <div className="sr-quick-stat-label">Win Rate</div>
              <div className="sr-quick-stat-value">{metrics.winRate}%</div>
            </div>
          </div>
          <div className="sr-quick-stat">
            <div className="sr-quick-stat-icon">💳</div>
            <div>
              <div className="sr-quick-stat-label">Collection Rate</div>
              <div className="sr-quick-stat-value">{metrics.collectionRate}%</div>
            </div>
          </div>
          <div className="sr-quick-stat">
            <div className="sr-quick-stat-icon">💰</div>
            <div>
              <div className="sr-quick-stat-label">Avg Deal Size</div>
              <div className="sr-quick-stat-value">{formatCurrency(metrics.avgDealSize)}</div>
            </div>
          </div>
          <div className="sr-quick-stat">
            <div className="sr-quick-stat-icon">⭐</div>
            <div>
              <div className="sr-quick-stat-label">Top Performer</div>
              <div className="sr-quick-stat-value">Priya Sharma</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesReports;